import { defaultTemplateId } from './../../utils/constants';
import axios from 'axios'
import snakecaseKeys from 'snakecase-keys'

import {
  AcceptedInviteUser,
  Company,
  Document,
  Model,
  Project,
  StripeProducts,
  TemplateDocument,
  User,
  UserWithAuthToken,
} from 'types'
import { appendToUrl } from 'utils/appendToUrl'
import { camelcaseKeys } from 'utils/camelcaseKeys'
import { downloadFile } from 'utils/downloadFile'
import { getBrowserUserAuth } from 'lib/BrowserStorage'

const COUNT_PER_PAGE = 20
const baseUrl = process.env.REACT_APP_API_URL
const authBase = `${baseUrl}/api/v1`


export const ROUTES = {
  acceptInvite: `${authBase}/invitation`,
  companies: `${authBase}/companies`,
  company: (companyId: number) => `${authBase}/companies/${companyId}`,
  companyManagerRole: (companyId: number|undefined) =>
    `${authBase}/companies/${companyId}/company_manager_roles`,
  otherRole: (projectId: number|undefined, roleType:string | undefined, templateDocumentIds?: string | number | undefined) =>
    `${authBase}/projects/${projectId}/${roleType}?template_directory_id=${templateDocumentIds || defaultTemplateId}`,
  deleteRole: (roleType:string, roleId:any, projectId?: number | undefined) =>
    `${authBase}/${roleType}/${roleId}?project_id=${projectId}`,
  deleteCompanyManagerRole: (companyId:number | undefined, roleId:any) =>
    `${authBase}/companies/${companyId}/company_manager_roles/${roleId}`,
  documents: (templateDocumentId: number) =>
    `${authBase}/template_documents/${templateDocumentId}/documents`,
  directories: (projectId: number) =>
    `${authBase}/projects/${projectId}/template_documents`,
  downloadDocuments: `${authBase}/document_downloads`,
  duplicateTemplateDocument: (templateDocumentId: number) =>
    `${authBase}/template_documents/${templateDocumentId}/duplicate`,
  moveTemplateDocument: (templateDocumentId: number) =>
    `${authBase}/template_documents/${templateDocumentId}/move_to_folder`,
  viewDirectories: (projectId: number | undefined) => 
    `${authBase}/projects/${projectId}/template_directories`,
  generateDocuments: (templateDocumentId: number) =>
    `${authBase}/template_documents/${templateDocumentId}/generate_documents`,
  login: `${authBase}/login`,
  projectCoordinatorsRole: (projectId: number) =>
    `${authBase}/projects/${projectId}/project_coordinator_roles`,
  projects: (companyId: number) =>
    `${authBase}/companies/${companyId}/projects`,
  resendInvitation: `${authBase}/reinvitations`,
  resetPassword: `${authBase}/reset_password`,
  signatoryRole: (projectId: number) =>
    `${authBase}/projects/${projectId}/signatory_roles`,
  signup: `${authBase}/signup`,
  stripeProducts: `${authBase}/stripe_products_prices`,
  templateDocument: (templateDocumentId: number) =>
    `${authBase}/template_documents/${templateDocumentId}`,
  templateDirectory: (directoryId: number | undefined) =>
    `${authBase}/template_directories/${directoryId}`,
  templateDocuments: (projectId: number, template_directory_id?: number) => {
    if (template_directory_id)
      return `${authBase}/projects/${projectId}/template_documents?template_directory_id=${template_directory_id}`
    else
      return `${authBase}/projects/${projectId}/template_documents`
  },
  templateDirectories: (projectId: number) =>
    `${authBase}/projects/${projectId}/template_directories`,
    templatesListDirectories: (projectId: number, directoryId: number) =>
    `${authBase}/projects/${projectId}/template_directories?template_directory_id=${directoryId}`,
  upgradePlan: `${authBase}/plans`,
  user: (userId: number) => `${authBase}/users/${userId}`,
  getPermission: (companyId: number | undefined, role_type: string | undefined) =>{
    return `${authBase}/companies/${companyId}/permissions?role_type =${role_type}`
  },
  putPermission: (companyId: number | undefined, id: number | undefined) =>{
    return `${authBase}/companies/${companyId}/permissions/${id}`
  },
  projectStatus: (id:any)=>{
    return `${authBase}/projects/${id}`
  }
}

export const HEADERS = {
  authorizedForm: (token: string) => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }),
  authorizedJSON: (token: string) => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }),
  json: { 'Content-Type': 'application/json' },
}

const getBase = (url: string) => {
  const authToken = getBrowserUserAuth()
  if (authToken) {
    const headers = HEADERS.authorizedJSON(authToken)
    return axios.get(url, { headers })
  } else {
    throw Error('User must be signed in.')
  }
}

const postBase = (
  url: string,
  body: any,
  type: 'form' | 'json' = 'json',
  config: object = {},
) => {
  const authToken = getBrowserUserAuth()
  if (authToken) {
    const headers =
      type === 'form'
        ? HEADERS.authorizedForm(authToken)
        : HEADERS.authorizedJSON(authToken)

    return axios.post(url, body, { headers, ...config })
  } else {
    throw Error('User must be signed in.')
  }
}

const patchBase = (url: string, body: any, type: 'form' | 'json' = 'json') => {
  const authToken = getBrowserUserAuth()
  if (authToken) {
    const headers =
      type === 'form'
        ? HEADERS.authorizedForm(authToken)
        : HEADERS.authorizedJSON(authToken)
    return axios.patch(url, body, { headers })
  } else {
    throw Error('User must be signed in.')
  }
}

const putBase = (url: string, body: any, type: 'form' | 'json' = 'json') => {
  const authToken = getBrowserUserAuth()
  if (authToken) {
    const headers =
      type === 'form'
        ? HEADERS.authorizedForm(authToken)
        : HEADERS.authorizedJSON(authToken)
    return axios.put(url, body, { headers })
  } else {
    throw Error('User must be signed in.')
  }
}

const deleteBase = (url: string) => {
  const authToken = getBrowserUserAuth()
  if (authToken) {
    const headers = HEADERS.authorizedJSON(authToken)
    return axios.delete(url, { headers })
  } else {
    throw Error('User must be signed in.')
  }
}

export const postProjectCoordinatorsRole = async (
  projectId: number,
  email: string,
): Promise<Project> => {
  const url = ROUTES.projectCoordinatorsRole(projectId)
  const body = { user: { email } }
  const { data } = await postBase(url, body)
  return camelcaseKeys<Project>(data)
}

export const postSignatoriesRole = async (
  projectId: number,
  email: string,
): Promise<Project> => {
  const url = ROUTES.signatoryRole(projectId)
  const body = { user: { email } }
  const { data } = await postBase(url, body)
  return camelcaseKeys<Project>(data)
}

export const patchAcceptInvite = async (
  invitationToken: string,
  userDetails: AcceptedInviteUser,
) => {
  const url = ROUTES.acceptInvite
  const params = snakecaseKeys({
    acceptInvitation: {
      invitationToken,
      ...userDetails,
    },
  })
  await axios.patch(url, params, { headers: HEADERS.json })
}

export const postTemplateDocument = async (
  projectId: number,
  formData: FormData,
): Promise<TemplateDocument> => {
  const url = ROUTES.templateDocuments(projectId)
  const { data } = await postBase(url, formData, 'form')
  return camelcaseKeys<TemplateDocument>(data)
}

export const postTemplateDirectory = async (
  projectId: number,
  formData: FormData,
): Promise<TemplateDocument> => {
  const url = ROUTES.templateDirectories(projectId)
  const { data } = await postBase(url, formData, 'form')
  return camelcaseKeys<TemplateDocument>(data)
}

export const postCompany = async (newCompanyData: {
  company: { name: string }
  payment: { payment_method_id: string; price_id: string }
}) => {
  const url = ROUTES.companies
  const { data } = await postBase(url, newCompanyData)
  return camelcaseKeys<Company>(data)
}

export const postProject = async (
  companyId: number,
  newProjectData: Pick<Project, 'name'>,
) => {
  const url = ROUTES.projects(companyId)
  const { data } = await postBase(url, newProjectData)
  return camelcaseKeys<Project>(data)
}

export const postDownloadDocuments = async (documentIds: number[]) => {
  const url = ROUTES.downloadDocuments
  const { data } = await postBase(
    url,
    { documents: { ids: documentIds } },
    'json',
    { responseType: 'arraybuffer' },
  )

  downloadFile(data)
}

export const getDocuments = async (
  templateDocumentId: number,
  pageNumber = 1,
  per = COUNT_PER_PAGE,
): Promise<Document[]> => {
  const params = { count: per, page: pageNumber }
  const documentsUrl = ROUTES.documents(templateDocumentId)
  const url = appendToUrl(documentsUrl, params)
  const {
    data: { documents },
  } = await getBase(url)
  return camelcaseKeys<Document[]>(documents)
}

export const getTemplateDocuments = async (
  projectId: number,
  template_directory_id?: number,
  pageNumber = 1,
  per = COUNT_PER_PAGE,
): Promise<TemplateDocument[]> => {
  console.log(template_directory_id, 'getTemplateDocuments')
  const params = { count: per, page: pageNumber }
  const templatesUrl = ROUTES.templateDocuments(projectId, template_directory_id)
  const url = appendToUrl(templatesUrl, params)
  const {
    data: { template_documents },
  } = await getBase(url)
  return camelcaseKeys<TemplateDocument[]>(template_documents)
}

export const getTemplateDirectories = async (
  projectId: number,
  templateId: number,
  pageNumber = 1,
  per = COUNT_PER_PAGE,
): Promise<Document[]> => {
  const params = { count: per, page: pageNumber, template_directory_id:templateId }
  const templatesUrl = ROUTES.directories(projectId)
  const url = appendToUrl(templatesUrl, params)
  const {
    data: { template_documents }
  } = await getBase(url)
  return camelcaseKeys<Document[]>(template_documents)
}

export const getTemplateDocument = async (
  templateDocumentId: number,
): Promise<TemplateDocument> => {
  const templateUrl = ROUTES.templateDocument(templateDocumentId)
  const { data } = await getBase(templateUrl)
  return camelcaseKeys<TemplateDocument>(data)
}

export const deleteTemplateDocument = async (
  templateDocumentId: number,
): Promise<TemplateDocument> => {
  const templateUrl = ROUTES.templateDocument(templateDocumentId)
  const { data } = await deleteBase(templateUrl)
  return camelcaseKeys<TemplateDocument>(data)
}

export const deleteDirecory = async (
  directoryId: number | undefined,
): Promise<any> => {
  const directoryUrl = ROUTES.templateDirectory(directoryId)
  const { data } = await deleteBase(directoryUrl)
  return camelcaseKeys<any>(data)
}

export const getProjects = async (companyId: number): Promise<Project[]> => {
  const url = ROUTES.projects(companyId)
  const {
    data: { projects },
  } = await getBase(url)
  return camelcaseKeys<Project[]>(projects)
}

export const getCompanies = async (): Promise<Company[]> => {
  const url = ROUTES.companies
  const {
    data: { companies },
  } = await getBase(url)
  return camelcaseKeys<Company[]>(companies)
}

export const getStripeProducts = async (): Promise<StripeProducts> => {
  const url = ROUTES.stripeProducts
  const { data } = await getBase(url)
  return camelcaseKeys<StripeProducts>(data)
}

export const getUser = async (userId: number): Promise<User | null> => {
  const url = ROUTES.user(userId)
  try {
    const { data } = await getBase(url)
    return camelcaseKeys<User>(data)
  } catch (error) {
    const {
      request: { status },
    } = error as any
    if (status === 401) {
      return null
    } else {
      throw error
    }
  }
}

export const patchResetPassword = (
  password: string,
  passwordConfirmation: string,
  token: string,
) => {
  const url = ROUTES.resetPassword
  const params = {
    user: { password, password_confirmation: passwordConfirmation, token },
  }
  return axios.patch(url, params, { headers: HEADERS.json })
}

export const patchUser = async (user: Exclude<User, Model>, id: number) => {
  const url = ROUTES.user(id)
  const { data } = await patchBase(url, snakecaseKeys(user))
  return camelcaseKeys<User>(data)
}

export const patchCompany = async (
  company: Pick<Company, 'name'>,
  id: number,
) => {
  const url = ROUTES.company(id)
  const { data } = await patchBase(url, { company })
  return camelcaseKeys<Company>(data)
}

export const patchTemplateDocument = async (formData: FormData, id: number) => {
  const url = ROUTES.templateDocument(id)
  const { data } = await patchBase(url, formData, 'form')
  return camelcaseKeys<TemplateDocument>(data)
}

export const moveTemplateDocument = async (
  template_directory_id: number,
  id: number,
) => {
  const params = {template_document: {"template_directory_id": template_directory_id}}
  const url = ROUTES.moveTemplateDocument(id)
  const { data } = await putBase(url, params)
  return camelcaseKeys<any>(data)
}

export const viewTemplateDirectories= async (
  projectId: number | undefined
 ) => {
  const url = ROUTES.viewDirectories(projectId)
  const { data } = await getBase(url)
  return camelcaseKeys<any>(data)
}

export const duplicateTemplateDocument = async (
  newProjectId: number,
  id: number,
) => {
  const params = { new_project_id: newProjectId }
  const url = ROUTES.duplicateTemplateDocument(id)
  const { data } = await postBase(url, params)
  return camelcaseKeys<TemplateDocument>(data)
}


export const duplicateTemplateFolder = async (
  newFolderId: number,
  id: number,
) => {
  const params = { template_directory_id: newFolderId }

  const url = ROUTES.duplicateTemplateDocument(id)
  const { data } = await postBase(url, params)
  return camelcaseKeys<TemplateDocument>(data)
}

export const postResetPassword = (email: string) => {
  const url = ROUTES.resetPassword
  const params = { user: { email } }
  return axios.post(url, params, { headers: HEADERS.json })
}

export const postLogin = async (email: string, password: string) => {
  const url = ROUTES.login
  const params = { user: { email, password } }
  const { data } = await axios.post(url, params, { headers: HEADERS.json })
  return camelcaseKeys<UserWithAuthToken>(data)
}

export const postSignup = async (formData: FormData) => {
  const url = ROUTES.signup
  const { data } = await axios.post(url, formData, { headers: HEADERS.json })
  return camelcaseKeys<UserWithAuthToken>(data)
}

export const postCompanyManagerRole = async (
  companyId: number | undefined,
  email: string,
): Promise<Company> => {
  const url = ROUTES.companyManagerRole(companyId)
  const body = { user: { email } }
  const { data } = await postBase(url, body)
  return camelcaseKeys<Company>(data)
}

export const postOtherRoles = async (
  projectId: number | undefined,
  templateDocumentIds: string | undefined,
  email: string,
  roleType: string | undefined
): Promise<any> => {
  const url = ROUTES.otherRole(projectId, roleType, templateDocumentIds)
  const body = { user: { email } }
  return  await postBase(url, body)
  // return camelcaseKeys<Company>(data)
}

export const getOtherRoles = async (
  projectId: number | undefined,
  templateDocumentIds: string | number | undefined,
  roleType: string | undefined
): Promise<any> => {
  const url = ROUTES.otherRole(projectId, roleType, templateDocumentIds)
  return  await getBase(url)
  // return camelcaseKeys<Company>(data)
}

export const getPermissions = async (
  companyId: number | undefined,
  roleType: string | undefined
): Promise<any> => {
  const url = ROUTES.getPermission(companyId, roleType)
  return  await getBase(url)
  // return camelcaseKeys<Company>(data)
}

export const updatePermissions = async (
  companyId: number | undefined,
  id: number | undefined,
  payload: any
): Promise<any> => {
  const url = ROUTES.putPermission(companyId, id)
  return  await patchBase(url,payload)
  // return camelcaseKeys<Company>(data)
}

export const updateProjectStatus = async (
  projectId: number | undefined,
  payload: any
): Promise<any> =>{
  const url = ROUTES.projectStatus(projectId)
  return  await putBase(url,payload)
}

export const deleteRoles = async (
  roleType: string,
  roleId: any,
  projectId?: number | undefined
): Promise<any> => {
  const url = ROUTES.deleteRole(roleType, roleId,projectId)
  return  await deleteBase(url)
  // return camelcaseKeys<Company>(data)
}
export const deleteCompanyMangerRoles = async (
  companyId: number | undefined,
  roleId: any
): Promise<any> => {
  const url = ROUTES.deleteCompanyManagerRole(companyId, roleId)
  return  await deleteBase(url)
  // return camelcaseKeys<Company>(data)
}
export const postReinvite = async (userId: number) => {
  const url = ROUTES.resendInvitation
  const body = { user: { user_id: userId } }
  const { data } = await postBase(url, body)
  return camelcaseKeys(data)
}

export const postUpgrade = async (
  stripeCustomerId: string,
  stripeProductId: string,
) => {
  const url = ROUTES.upgradePlan
  const body = { stripeCustomerId, stripeProductId }
  await postBase(url, body)
}

export const createDirectoryOrSubDirectory = async (
  projectId: number,
  formData: FormData,
): Promise<TemplateDocument> => {
  const url = ROUTES.templateDirectories(projectId)
  const { data } = await postBase(url, formData, 'form')
  return camelcaseKeys<TemplateDocument>(data)
}

export const deleteDirectory = async (
  projectId: number,
  directoryId: number,
): Promise<any> => {
  const url = ROUTES.templateDirectories(projectId)
  const { data } = await deleteBase(`${url}/${directoryId}`)
  return camelcaseKeys<any>(data)
}

export const getTemplatesFolder = async (
  projectId: number,
  directoryId: number,
): Promise<any> => {
  const url = ROUTES.templatesListDirectories(projectId, directoryId)
  const { data } = await getBase(`${url}/${directoryId}`)
  return camelcaseKeys<any>(data)
}